import { useLayoutEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { FileText, Upload, X } from 'lucide-react';
import { useUpdateDocument, useUploadDocument } from '../../hooks/useApi';
import { errorMessage } from '../../services/api';
import { toast } from '../../store';
import type { DocType, Document } from '../../types';
import { cn, DOC_TYPE_LABELS, formatFileSize, toOptions } from '../../utils/helpers';
import { Button } from '../ui/Button';
import { Input, Select, Textarea } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { CarSelect } from './EntitySelects';
import { t } from '../../i18n';

const MAX_SIZE = 50 * 1024 * 1024;
const ACCEPT = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
};

interface UploadValues {
  car_id: string;
  title: string;
  doc_type: DocType;
  expiry_date: string;
  notes: string;
}

export function DocumentUploadModal({ isOpen, onClose, carId }: { isOpen: boolean; onClose: () => void; carId?: number }) {
  const upload = useUploadDocument();
  const [files, setFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState<number | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UploadValues>();

  useLayoutEffect(() => {
    if (isOpen) {
      setFiles([]);
      setProgress(null);
      reset({ car_id: carId ? String(carId) : '', title: '', doc_type: 'other', expiry_date: '', notes: '' });
    }
  }, [isOpen, carId, reset]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ACCEPT,
    maxSize: MAX_SIZE,
    onDrop: (accepted, rejected) => {
      setFiles((prev) => [...prev, ...accepted]);
      if (rejected.length) toast.error(t('{rejected} súbor(ov) nie je možné nahrať – nepodporovaný typ alebo väčší ako 50 MB', { rejected: rejected.length }));
    },
  });

  const busy = progress !== null;

  const onSubmit = async (values: UploadValues) => {
    if (!files.length) {
      toast.error(t('Vyberte aspoň jeden súbor'));
      return;
    }
    let uploaded = 0;
    try {
      for (const [index, file] of files.entries()) {
        setProgress(0);
        await upload.mutateAsync({
          file,
          data: {
            car_id: carId ?? Number(values.car_id),
            // A shared title only makes sense for a single file
            title: files.length === 1 && values.title ? values.title : undefined,
            doc_type: values.doc_type,
            expiry_date: values.expiry_date || undefined,
            notes: values.notes || undefined,
          },
          onProgress: (p) => setProgress(Math.round(((index + p / 100) / files.length) * 100)),
        });
        uploaded++;
      }
      toast.success(uploaded === 1 ? t('Dokument bol nahraný') : t('Nahraných {uploaded} dokumentov', { uploaded }));
      onClose();
    } catch (error) {
      toast.error(errorMessage(error, t('Nahrávanie zlyhalo')) + (uploaded ? t(' (nahraných {uploaded} z {files})', { uploaded, files: files.length }) : ''));
      setFiles((prev) => prev.slice(uploaded));
    } finally {
      setProgress(null);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      busy={busy}
      size="lg"
      title={t('Nahrať dokumenty')}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {t('Zrušiť')}
          </Button>
          <Button type="submit" form="upload-form" loading={busy} disabled={!files.length}>
            <Upload className="h-4 w-4" />
            {busy ? t('Nahrávanie {progress} %', { progress }) : `${t('Nahrať')}${files.length ? ` (${files.length})` : ''}`}
          </Button>
        </>
      }
    >
      <form id="upload-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <div
          {...getRootProps()}
          className={cn(
            'cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-colors',
            isDragActive ? 'border-primary-500 bg-primary-50' : 'border-secondary-300 hover:border-primary-400',
          )}
        >
          <input {...getInputProps()} />
          <Upload className={cn('mx-auto mb-2 h-10 w-10', isDragActive ? 'text-primary-500' : 'text-secondary-400')} />
          <p className="font-medium text-secondary-700">{isDragActive ? t('Pustite súbory sem') : t('Pretiahnite súbory sem alebo kliknite pre výber')}</p>
          <p className="mt-1 text-xs text-secondary-500">{t('PDF, obrázky, Word, Excel · max. 50 MB')}</p>
        </div>

        {files.length > 0 && (
          <ul className="max-h-40 divide-y divide-secondary-100 overflow-y-auto rounded-lg border border-secondary-200">
            {files.map((file, index) => (
              <li key={`${file.name}-${index}`} className="flex items-center gap-3 px-3 py-2 text-sm">
                <FileText className="h-4 w-4 shrink-0 text-secondary-400" />
                <span className="min-w-0 flex-1 truncate">{file.name}</span>
                <span className="text-xs text-secondary-500">{formatFileSize(file.size)}</span>
                <button
                  type="button"
                  className="icon-btn"
                  disabled={busy}
                  onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                  aria-label={t('Odobrať {name}', { name: file.name })}
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <CarSelect
            label={t('Vozidlo')}
            required
            disabled={!!carId}
            error={errors.car_id?.message}
            {...register('car_id', { required: carId ? false : t('Vyberte vozidlo') })}
          />
          <Select label={t('Typ dokumentu')} required options={toOptions(DOC_TYPE_LABELS)} {...register('doc_type')} />
          <Input
            label={t('Názov')}
            placeholder={files.length > 1 ? t('Použije sa názov súboru') : t('Predvolene: názov súboru')}
            disabled={files.length > 1}
            {...register('title')}
          />
          <Input label={t('Platnosť do')} type="date" hint={t('Upozornenie 30 dní pred koncom platnosti')} {...register('expiry_date')} />
          <Textarea label={t('Poznámky')} rows={2} wrapperClassName="sm:col-span-2" {...register('notes')} />
        </div>
      </form>
    </Modal>
  );
}

interface EditValues {
  title: string;
  doc_type: DocType;
  expiry_date: string;
  notes: string;
}

export function DocumentEditModal({ document, onClose }: { document: Document | null; onClose: () => void }) {
  const update = useUpdateDocument();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditValues>();

  useLayoutEffect(() => {
    if (document) {
      reset({
        title: document.title,
        doc_type: document.doc_type,
        expiry_date: document.expiry_date ?? '',
        notes: document.notes ?? '',
      });
    }
  }, [document, reset]);

  const onSubmit = async (values: EditValues) => {
    if (!document) return;
    try {
      await update.mutateAsync({
        id: document.id,
        data: { ...values, expiry_date: values.expiry_date || null, notes: values.notes || null },
      });
      toast.success(t('Dokument bol uložený'));
      onClose();
    } catch (error) {
      toast.error(errorMessage(error, t('Dokument sa nepodarilo uložiť')));
    }
  };

  return (
    <Modal
      isOpen={!!document}
      onClose={onClose}
      busy={update.isPending}
      title={t('Upraviť dokument')}
      description={document?.original_filename}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={update.isPending}>
            {t('Zrušiť')}
          </Button>
          <Button type="submit" form="document-edit-form" loading={update.isPending}>
            {t('Uložiť')}
          </Button>
        </>
      }
    >
      <form id="document-edit-form" onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 gap-4 sm:grid-cols-2" noValidate>
        <Input label={t('Názov')} required wrapperClassName="sm:col-span-2" error={errors.title?.message} {...register('title', { required: t('Zadajte názov') })} />
        <Select label={t('Typ dokumentu')} options={toOptions(DOC_TYPE_LABELS)} {...register('doc_type')} />
        <Input label={t('Platnosť do')} type="date" {...register('expiry_date')} />
        <Textarea label={t('Poznámky')} rows={2} wrapperClassName="sm:col-span-2" {...register('notes')} />
      </form>
    </Modal>
  );
}
